import { Check } from "lucide-react";

// Per-client wizard step keys. Kept as a typed alias so existing callers
// continue to enjoy strict typing, but the component is generic over K.
export type MigrationStepKey = "census" | "plans" | "enrollments";

export type MigrationStepStatus =
  | "pending"
  | "uploaded"
  | "previewing"
  | "confirmed"
  | "skipped"
  | "error";

interface StepDef<K extends string> {
  key: K;
  label: string;
  status: MigrationStepStatus;
}

interface Props<K extends string> {
  steps: StepDef<K>[];
  active: K;
  onChange: (next: K) => void;
}

/**
 * Variant A's circular-numeral stepper (per design handoff).
 *
 * Three equal segments with: numeral circle (28px) + step title + optional
 * status pill. Done segments show a green check; the active one is dark; the
 * rest are muted ghost.
 *
 * Click to navigate — gated only by step status (always allowed to go back to
 * a confirmed step; can't jump forward to a step whose predecessor isn't
 * confirmed). The wizard's outer auto-advance logic still drives the active
 * key after each successful confirm.
 */
export function MigrationStepper<K extends string>({
  steps,
  active,
  onChange,
}: Props<K>) {
  // Tailwind needs literal class names — pick from a small whitelist instead
  // of building one dynamically (otherwise JIT purges them).
  const colsByCount: Record<number, string> = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
  };
  const cols = colsByCount[steps.length] ?? "grid-cols-4";
  return (
    <div
      className={`grid ${cols} overflow-hidden rounded-md border bg-card`}
    >
      {steps.map((s, i) => {
        const isActive = s.key === active;
        const isDone = s.status === "confirmed" || s.status === "skipped";
        const prev = i > 0 ? steps[i - 1] : null;
        const prevDone =
          prev !== null &&
          (prev.status === "confirmed" || prev.status === "skipped");
        const reachable = i === 0 || isDone || isActive || prevDone;

        return (
          <button
            type="button"
            key={s.key}
            disabled={!reachable}
            onClick={() => reachable && onChange(s.key)}
            className={`relative flex items-center gap-3 px-4 py-3 text-left transition-colors ${
              i > 0 ? "border-l" : ""
            } ${
              isActive
                ? "bg-stone-50/60 dark:bg-stone-900/30"
                : reachable
                  ? "hover:bg-stone-50/40 dark:hover:bg-stone-900/20"
                  : "cursor-not-allowed opacity-60"
            }`}
          >
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                isDone
                  ? "bg-emerald-600 text-white"
                  : isActive
                    ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
                    : "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400"
              }`}
            >
              {isDone ? (
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              ) : (
                i + 1
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div
                className={`text-sm font-medium leading-tight ${
                  isActive || isDone
                    ? "text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                {s.label}
              </div>
              <div className="text-[11px] leading-tight text-muted-foreground">
                {labelForStatus(s.status, isActive)}
              </div>
            </div>
            {isActive && (
              <span
                aria-hidden
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-stone-900 dark:bg-stone-100"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function labelForStatus(s: MigrationStepStatus, isActive: boolean): string {
  if (s === "confirmed") return "Done";
  if (s === "skipped") return "Skipped";
  if (s === "previewing") return isActive ? "Ready to review" : "Pending review";
  if (s === "error") return "Error";
  return "Not started";
}
